import type { ReactNode } from "react";

import { createContext, useContext, useEffect, useMemo, useState } from "react";

import type { RepSegment } from "../models";

import { useRepSegmentsApi } from "../api/repSegmentsApi";
import { useRepContext } from "./editableItemContext";

export interface SegmentContextType {
	segmentId: number | undefined;
	setSegmentId: (id: number) => void;
	segment: RepSegment;
}

const defaultContextValues = {
	segmentId: undefined,
	setSegmentId: undefined,
	segment: undefined,
};

export const SegmentContext = createContext<SegmentContextType>(defaultContextValues);

export const useSegmentContext = () => useContext(SegmentContext);

interface SegmentProviderProps {
	children: ReactNode;
	initialSegmentId?: number;
}

export function SegmentProvider(props: SegmentProviderProps) {
	const { children, initialSegmentId } = props;

	const { repId } = useRepContext();
	const { useRepSegmentsQuery } = useRepSegmentsApi(repId);
	const { data: repSegments } = useRepSegmentsQuery();
	const [segmentId, setSegmentId] = useState<number | undefined>(initialSegmentId);

	useEffect(() => {
		if (initialSegmentId !== undefined) {
			setSegmentId(initialSegmentId);
		}
	}, [initialSegmentId]);

	const segment = useMemo(() => repSegments?.find((s) => s.id === segmentId), [repSegments, segmentId]);

	const contextValue = useMemo(() => ({ segmentId, setSegmentId, segment }), [segmentId, segment]);

	return <SegmentContext.Provider value={contextValue}>{children}</SegmentContext.Provider>;
}
